import { useState } from "react";
import type * as React from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useHistory } from "react-router-dom";
import AccountExitLink from "components/v3/sharedComponents/AccountExitLink";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";
import GLButton from "components/v3/sharedComponents/GLButton";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import { COLORS } from "components/v3/Theme/colors";
import { GENOMIC_LIFE_COMPLETE_MEMBER_FEATURES } from "constants/genomicLifeCompleteProgramCopy";
import { MEMBERSHIP_ANNUAL_PRICE_USD } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";

const formattedAnnualPrice = new Intl.NumberFormat("en-US", {
  currency: "USD",
  style: "currency"
}).format(MEMBERSHIP_ANNUAL_PRICE_USD);

const sectionCardSx = {
  bgcolor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box",
  p: { sm: 3, xs: 2.5 },
  width: "100%"
} as const;

const inlinePolicyLinkSx = {
  color: COLORS.DARK_GRAY,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": { color: COLORS.PRIMARY_DARK }
} as const;

const ExplanationOfBenefitsPage: React.FC = () => {
  const history = useHistory();
  const [agreedAutoRenew, setAgreedAutoRenew] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const canContinue = agreedAutoRenew && agreedTerms;

  PageTitle("Continue your membership");

  const handleContinue = () => {
    if (!canContinue) return;
    history.push(PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_PAYMENT);
  };

  return (
    <GradientFramedLayout showHeader showNav={false}>
      <Box
        sx={{
          alignItems: "stretch",
          alignSelf: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 740, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <AccountExitLink to={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION} />

        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          Continue your membership
        </Typography>

        <Box sx={sectionCardSx}>
          <Typography
            component="h2"
            variant="body_strong_lg_black"
            sx={{ mb: 3 }}
          >
            Genomic Life Complete
          </Typography>
          <Typography variant="body" sx={{ mb: 2 }}>
            By reactivating your membership, you will have access to:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2
            }}
          >
            {GENOMIC_LIFE_COMPLETE_MEMBER_FEATURES.map((feature, index) => (
              <Typography key={feature.label} variant="body">
                <Box
                  component="span"
                  sx={{ fontWeight: index === 0 ? 800 : 700 }}
                >
                  {feature.label}
                </Box>{" "}
                {feature.body}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box sx={sectionCardSx}>
          <Typography
            component="h2"
            variant="body_strong_lg_black"
            sx={{ mb: 1.5 }}
          >
            Was your spouse covered under your plan?
          </Typography>
          <Typography variant="body">
            They can reactivate their membership by logging into their own
            portal account and navigating to Plan Information. There, they
            should see a &quot;Reactivate membership&quot; button.
          </Typography>
        </Box>

        <Box sx={sectionCardSx}>
          <Box
            sx={{
              alignItems: { sm: "flex-start", xs: "stretch" },
              display: "flex",
              flexDirection: { sm: "row", xs: "column" },
              gap: { sm: 4, xs: 2 },
              justifyContent: "space-between",
              mb: 2
            }}
          >
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
              <Typography variant="body_strong_sm">Your yearly cost</Typography>
              <Typography
                variant="body_strong_lg"
                sx={{
                  color: COLORS.ACCENT2,
                  fontSize: "20px",
                  fontWeight: 700
                }}
              >
                {formattedAnnualPrice}
              </Typography>
            </Box>
            <Typography
              component="p"
              variant="body_dark_gray"
              sx={{
                flexShrink: 0,
                m: 0,
                mt: -1,
                pt: { sm: 0.5, xs: 0 },
                textAlign: { sm: "right", xs: "left" }
              }}
            >
              Auto-renews yearly
              <br />
              Cancel in 1 year
            </Typography>
          </Box>
          <Typography variant="body_dark_gray" sx={{ m: 0 }}>
            Subscriptions are subject to our refunds and cancellation policies
            and automatically renew each year. Learn more in our{" "}
            <Box
              component={RouterLink}
              rel="noopener noreferrer"
              target="_blank"
              to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
              sx={inlinePolicyLinkSx}
            >
              Terms of Use
            </Box>
            .
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FormControlLabel
            data-testid="eob-checkbox-auto-renew"
            control={
              <Checkbox
                checked={agreedAutoRenew}
                onChange={(e) => setAgreedAutoRenew(e.target.checked)}
                sx={{ alignSelf: "flex-start", mt: -1.2 }}
              />
            }
            label={
              <Typography variant="body_strong">
                By checking here you agree to reactivate your account and that
                this account will renew automatically on an annual basis.
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
              gap: 1,
              m: 0,
              "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 }
            }}
          />
          <FormControlLabel
            data-testid="eob-checkbox-terms"
            control={
              <Checkbox
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                sx={{ alignSelf: "flex-start", mt: -1.3 }}
              />
            }
            label={
              <Typography variant="body_strong">
                By checking here you agree to the Genomic Life{" "}
                <Box
                  component={RouterLink}
                  rel="noopener noreferrer"
                  target="_blank"
                  to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={inlinePolicyLinkSx}
                >
                  Terms of Use
                </Box>{" "}
                and{" "}
                <Box
                  component={RouterLink}
                  rel="noopener noreferrer"
                  target="_blank"
                  to={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_POLICY}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={inlinePolicyLinkSx}
                >
                  Privacy Policy
                </Box>
                .
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
              gap: 1,
              m: 0,
              "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 }
            }}
          />
        </Box>

        <Box
          sx={{
            alignItems: { sm: "flex-start", xs: "stretch" },
            display: "flex",
            flexDirection: { sm: "row", xs: "column" },
            flexWrap: "wrap",
            gap: { sm: 3, xs: 2 },
            justifyContent: "flex-start"
          }}
        >
          <GLButton
            buttonType="primary"
            disabled={!canContinue}
            onClick={handleContinue}
            rightIcon={<ArrowForward aria-hidden />}
            text="Continue"
            sx={{
              maxWidth: { sm: "none", xs: "100%" },
              width: { sm: "auto", xs: "100%" }
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              marginLeft: { sm: "auto", xs: 0 },
              maxWidth: { sm: 360, xs: "100%" },
              minWidth: 0,
              textAlign: "right",
              width: { xs: "100%" }
            }}
          >
            <Typography variant="body_strong_dark_gray" sx={{ mb: -0.5 }}>
              Have questions?
            </Typography>
            <Typography variant="body_dark_gray" sx={{ m: 0 }}>
              Don&apos;t hesitate to{" "}
              <Box
                component={RouterLink}
                rel="noopener noreferrer"
                target="_blank"
                to={PATHS.HELP_CENTER_CONTACT_US}
                sx={{
                  ...inlinePolicyLinkSx,
                  display: "inline"
                }}
              >
                Contact us
              </Box>{" "}
              for more details.
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            borderBottomWidth: "3px",
            borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2),
            my: 2
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pb: 1
          }}
        >
          <Typography variant="body_dark_gray" sx={{ m: 0 }}>
            Renewing a gift membership is not currently possible online—but our
            team would be happy to help.
          </Typography>
          <GenomicLifeLegalDisclaimer typographyVariant="body_dark_gray" />
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default ExplanationOfBenefitsPage;
